import OpenInNew from "@mui/icons-material/OpenInNew";
import { IconButton, Stack } from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  StaffPipelinesTableQuery,
  StaffPipelinesTableQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";
import { STAFF_PIPELINES_TABLE_QUERY } from "./api";
import { PipelineDialog } from "./PipelineDialog";

type PipelineRow = StaffPipelinesTableQuery["pipelines"]["items"][number];

export const Pipelines = () => {
  useBreadcrumb({ label: "Pipelines" });

  const {
    selectedId: pipelineId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("pipelineId");

  const columns: OmnisearchGridColDef<PipelineRow>[] = useMemo(
    () => [
      {
        headerName: "Organization",
        field: "tenant",
        flex: 1.2,
        sortable: false,
        filterKeyType: "multipleTenant",
        valueGetter(_, row) {
          return row.tenant?.name ?? "";
        },
      },
      {
        headerName: "Pipeline Name",
        field: "name",
        flex: 1.2,
      },
      {
        headerName: "Last Run",
        field: "lastRunAt",
        filterKeyType: "time",
        flex: 1,
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "Never";
        },
      },
      {
        headerName: "Created At",
        field: "createdAt",
        flex: 1.2,
        filterKeyType: "time",
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        headerName: "Created By",
        field: "createdBy",
        flex: 1.2,
      },
      {
        headerName: "Updated At",
        field: "updatedAt",
        filterKeyType: "time",
        flex: 1.2,
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        type: "actions",
        field: "actions",
        getActions: (row) => {
          return [
            <IconButton
              key="open"
              component={Link}
              to={`./${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNew />
            </IconButton>,
          ];
        },
      },
    ],
    []
  );

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<
        StaffPipelinesTableQuery,
        StaffPipelinesTableQueryVariables,
        PipelineRow
      >
        persistenceKey="staffPipelines"
        isURLDriven
        rowHeight="auto"
        initialSortModel={[{ field: "updatedAt", sort: "desc" }]}
        onRowClick={(params) => {
          pushIdToUrl(params.row.id);
        }}
        columns={columns}
        dataQuery={STAFF_PIPELINES_TABLE_QUERY}
        getItems={(data) => data.pipelines.items}
        getCount={(data) => data.pipelines.count}
      />
      {!!pipelineId && (
        <PipelineDialog
          pipelineId={pipelineId}
          onClose={() => {
            popIdFromUrl();
          }}
        />
      )}
    </Stack>
  );
};
